import { KycData, KycIdImage } from "@/types/kycReview";
import {
  HEIC_HEIF_EXTENSIONS,
  isAcceptedUploadFile,
} from "@/utils/uploadFiles";
import { hasValue } from "./paths";

export interface AttachmentType {
  image: string;
  name: string;
}

// Frontend attachment slots accepted by POST /v2/kyc-review/image.
export const attachmentTypes: AttachmentType[] = [
  { image: "idPhotoFront", name: "Front side of the ID" },
  { image: "idPhotoBack", name: "Back side of the ID" },
  { image: "secondary1", name: "Valid Secondary ID #1" },
  { image: "secondary2", name: "Valid Secondary ID #2" },
  { image: "PEPDocument", name: "PEP supporting document" },
  { image: "marriageCert", name: "Marriage certificate" },
  { image: "signature", name: "Signature" },
  { image: "proofOfAddress", name: "Proof of address" },
];

export const attachmentTypeByImage: Record<string, AttachmentType> =
  attachmentTypes.reduce(
    (result, attachmentType) => ({
      ...result,
      [attachmentType.image]: attachmentType,
    }),
    {} as Record<string, AttachmentType>
  );

// Accepted MIME types (matches the backend allowlist; 20 MB cap enforced there).
export const acceptedAttachmentMime: string[] = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/heic",
  "image/heif",
  "application/pdf",
];

export const MAX_ATTACHMENT_BYTES = 20 * 1000 * 1000; // 20 MB

const imageAliases: Record<string, string[]> = {
  idPhotoFront: ["idPhotoFront", "secondary1"],
  idPhotoBack: ["idPhotoBack", "secondary2"],
  secondary1: ["secondary1", "idPhotoFront"],
  secondary2: ["secondary2", "idPhotoBack"],
};

function imageKeys(imageType: string): string[] {
  return imageAliases[imageType] || [imageType];
}

export const acceptedAttachmentExtensions = [
  ".png",
  ".jpg",
  ".jpeg",
  ".webp",
  ...HEIC_HEIF_EXTENSIONS,
  ".pdf",
];

// react-dropzone v10 accepts an array of MIME types and extensions.
export const dropzoneAccept = [
  ...acceptedAttachmentMime,
  ...acceptedAttachmentExtensions,
];

export function isAcceptedAttachmentFile(file: File): boolean {
  return isAcceptedUploadFile(
    file,
    acceptedAttachmentMime,
    acceptedAttachmentExtensions
  );
}

// Returns the on-file image metadata for a given slot, if the approved record
// already carries it (either as a direct field or inside idImages[]).
export function getKycImage(
  kyc: KycData | undefined,
  imageType: string
): KycIdImage | null {
  if (!kyc) return null;
  const keys = imageKeys(imageType);
  for (const key of keys) {
    const direct = (kyc as Record<string, unknown>)[key];
    if (direct && typeof direct === "object") return direct as KycIdImage;
  }
  const fromArray = (kyc.idImages || []).find(
    (item) => item?.image && keys.includes(item.image)
  );
  return fromArray || null;
}

export function hasOnFileImage(
  kyc: KycData | undefined,
  imageType: string
): boolean {
  return hasValue(getKycImage(kyc, imageType));
}

const PREVIEWABLE_IMAGE_EXTENSIONS = [
  "png",
  "jpg",
  "jpeg",
  "webp",
  "gif",
  "bmp",
];

// True when a document can be rendered inline as an <img>. HEIC/HEIF and PDF
// are not reliably browser-previewable, so callers fall back to a "view" link.
export function isPreviewableImage(
  meta?: Pick<KycIdImage, "mimeType" | "fileName" | "url"> | null
): boolean {
  if (!meta) return false;
  const mime = (meta.mimeType || "").toLowerCase();
  if (mime)
    return (
      mime.startsWith("image/") &&
      mime !== "image/heic" &&
      mime !== "image/heif"
    );
  const source = (meta.fileName || meta.url || "").toLowerCase().split("?")[0];
  const ext = source.includes(".") ? source.split(".").pop() || "" : "";
  return PREVIEWABLE_IMAGE_EXTENSIONS.includes(ext);
}
